import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { OrganizationItem } from '../../api/types';
import { ROUTES } from '../../shared/constants';
import orgSwitcherSlice from '../redux/slice';

export type OrgSwitchedDialogProps = {
    org?: OrganizationItem;
};

// Org Switcher feature - confirmation shown after a successful switch. A toast was easy to miss
// for a change this consequential (everything the user sees/does next is scoped to the new org),
// so this surfaces as a dialog instead, with the same "here's what you can do" shortcut-list
// treatment as CreateOrderIntentModal, so the two feel like one design language rather than two.
export const OrgSwitchedDialog: React.FC<OrgSwitchedDialogProps> = ({ org }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleClose = () => dispatch(orgSwitcherSlice.actions.setLastSwitchedOrg(undefined));

    const handleNavigate = (route: string) => {
        handleClose();
        navigate(route);
    };

    return (
        <Dialog open={Boolean(org)} onClose={handleClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: '10px' } }}
        >
            <DialogContent sx={{ pt: 3, pb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '4px' }}>
                    <CheckCircleIcon sx={{ color: '#16A34A', fontSize: '22px' }} />
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A2332' }}>
                        Switched to {org?.name}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '12.5px', color: '#64748B', mb: 2, ml: '32px' }}>
                    Everything below is scoped to this organization. Here's what you can do:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <ShortcutOption
                        icon={<ReceiptLongOutlinedIcon />}
                        title="Test Submissions"
                        description="View and manage this organization's test submissions."
                        onClick={() => handleNavigate(ROUTES.Orders)}
                    />
                    {org?.preBuyEnabled && (
                        <ShortcutOption
                            icon={<LocalShippingOutlinedIcon />}
                            title="Order Testing Supplies"
                            description="Bulk order testing supplies to this clinic."
                            onClick={() => handleNavigate(ROUTES.KitOrderShipToClinic)}
                        />
                    )}
                    <ShortcutOption
                        icon={<PeopleAltOutlinedIcon />}
                        title="Manage Team"
                        description="Add, edit, or remove users for this organization."
                        onClick={() => handleNavigate(ROUTES.Organizations)}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

type ShortcutOptionProps = {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
};

// Same row treatment as CreateOrderIntentModal's IntentOption - reused visually (not literally
// shared, since that one carries its own disabled/tooltip logic this dialog doesn't need).
const ShortcutOption: React.FC<ShortcutOptionProps> = ({ icon, title, description, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 14px',
            borderRadius: '8px',
            border: '1px solid #DDE4EC',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            '&:hover': { borderColor: '#0891B2', backgroundColor: '#F0FAFB' },
            '& svg': { fontSize: '20px', color: '#0891B2', mt: '2px' },
        }}
    >
        {icon}
        <Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#1A2332' }}>{title}</Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#64748B', mt: '2px' }}>{description}</Typography>
        </Box>
    </Box>
);
